/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { remarksSchema } from './common';
import { excludeTabAndNewline } from './regex';

import {
  CompanyResponse,
  ShopResponse,
  WalletServiceAutocompleteResponse,
  WalletServiceResponse,
} from '@/repositories/generated';
import {
  TGrantTypeKey,
  ExpiredPatternKey,
  UseStartPatternKey,
  AccountTypeKey,
  TargetSelectionKey,
  ActionTypeKey,
} from '@/types';
import {
  InferType,
  string,
  date,
  number,
  array,
  object,
  AnyObject,
  ObjectSchema,
  boolean,
} from '@/validations/custom-yup';

/** キャンペーン名 */
export const campaignNameSchema = string()
  .label('キャンペーン名')
  .required()
  .max(50)
  .matches(excludeTabAndNewline, 'タブ・改行文字を削除してください。');

/** 管理名称 */
export const systemNameSchema = string()
  .label('管理名称')
  .required()
  .max(30)
  .matches(excludeTabAndNewline, 'タブ・改行文字を削除してください。');

/** キャンペーン期間（開始日時） */
export const campaignStartDateTimeSchema = date()
  .label('開始日時')
  .required()
  .min(new Date())
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined));

/** キャンペーン期間（終了日時） */
export const campaignEndDateTimeSchema = date()
  .label('終了日時')
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined))
  .when(
    'startDateTime',
    ([startDateTime], schema) => (
      startDateTime instanceof Date
        ? schema
          .test(
            'isMoreThanStartDate',
            '終了日時は開始日時より後の日時を入力してください。',
            (value) => (value ? startDateTime.getTime() < value.getTime() : true),
          )
          .min(startDateTime, '終了日時は開始日時より後の日時を入力してください。')
        : schema
    ),
  );

/** ウォレットサービス */
export const walletServiceSchema: ObjectSchema<WalletServiceAutocompleteResponse, AnyObject, undefined, 'd'> = object({
  id: string()
    .required(),
  name: string()
    .required(),
  bonusExpiredFlg: boolean()
    .required(),
})
  .label('ウォレットサービス')
  .required(({ label }) => `${label}を選択してください。`)
  .default(undefined);

/** ウォレットサービス（複数） */
export const walletServicesSchema = array<AnyObject, WalletServiceResponse>(object({
  id: string()
    .required(),
  name: string()
    .required(),
})
  .required())
  .default([] as WalletServiceResponse[])
  .label('ウォレットサービス')
  .min(1);

/** アクション区分 */
export const actionTypeSchema = string<ActionTypeKey>()
  .label('アクション区分')
  .required(({ label }) => `${label}を選択してください。`);

/** ハウス決済 */
export const houseFlgSchema = boolean().label('ハウス決済')
  .nullable()
  .defined()
  .when(
    'actionType',
    ([actionType], schema) => (
      actionType === 'PREPAID_SETTLEMENT' || actionType === 'DIRECT_BANK_SETTLEMENT'
        ? boolean().required()
        : schema
    ),
  );
/** Smart Code決済 */
export const smartCodeFlgSchema = boolean().label('Smart Code決済')
  .nullable()
  .defined()
  .when(
    'actionType',
    ([actionType], schema) => (
      actionType === 'PREPAID_SETTLEMENT' || actionType === 'DIRECT_BANK_SETTLEMENT'
        ? boolean().required()
        : schema
    ),
  );
/** 対象企業・店舗 */
export const targetSelectionSchema = string<TargetSelectionKey>()
  .label('対象企業・店舗');

/** 対象企業 */
export const companiesSchema = array<AnyObject, CompanyResponse>(object({
  id: string()
    .required(),
  name: string()
    .required(),
})
  .required())
  .label('企業')
  .default([]);

/** 対象店舗 */
export const shopsSchema = array<AnyObject, ShopResponse>(object()
  .shape({
    id: string()
      .required(),
    name: string()
      .required(),
  })
  .required())
  .label('店舗')
  .default([]);

/** 店舗CSVファイル名 */
export const campaignFileNameSchema = string()
  .label('ファイル名')
  .transform((value, originalValue) => (typeof originalValue === 'string' ? value : undefined));

/** 付与タイミング */
export const useStartPatternSchema = string<UseStartPatternKey>()
  .label('付与タイミング')
  .required(({ label }) => `${label}を選択してください。`);

/** 付与タイミング（日数） */
export const useStartAddValueSchema = number()
  .label('日数')
  .transform((value, originalValue) => (typeof originalValue === 'number' ? value : undefined))
  .when(
    'useStartPattern',
    ([useStartPattern], schema) => (
      useStartPattern as UseStartPatternKey === 'DAYS_LATER'
        ? schema
          .required()
          .integer()
          .min(1)
          .transform((value, originalValue) => (originalValue === '' ? undefined : value))
          .typeError(({ label }) => `${label}は整数で入力してください。`)
        : schema
    ),
  );

/** 付与口座 */
export const accountTypeSchema = string<AccountTypeKey>()
  .label('付与口座')
  .required(({ label }) => `${label}を選択してください。`)
  .transform((value?: string | { value: AccountTypeKey }) => (typeof value === 'string' ? value : value?.value));

/** 付与率/付与数（下限金額） */
export const amountFromSchema = number()
  .label('下限金額')
  .required()
  .integer()
  .min(1)
  .transform((value, originalValue) => (originalValue === '' ? undefined : value))
  .typeError(({ label }) => `${label}は整数で入力してください。`);

/** 付与率/付与数（上限金額） */
export const amountToSchema = number()
  .label('上限金額')
  .required()
  .integer()
  .min(1)
  .transform((value, originalValue) => (originalValue === '' ? undefined : value))
  .typeError(({ label }) => `${label}は整数で入力してください。`)
  .when(
    'amountFrom',
    ([amountFrom], schema) => (
      typeof amountFrom === 'number'
        ? schema.moreThan(amountFrom, ({ label }) => `${label}は下限金額より大きい数字を入力してください。`)
        : schema
    ),
  );

/** 付与区分 */
export const grantTypeSchema = string<TGrantTypeKey>()
  .label('付与区分')
  .required(({ label }) => `${label}を選択してください。`)
  .transform((value?: string | { value: TGrantTypeKey }) => (typeof value === 'string' ? value : value?.value));

/** 付与率 */
export const rateSchema = number()
  .label('付与率（%）')
  .transform((value, originalValue) => (typeof originalValue === 'number' ? value : undefined))
  .when(
    'grantType',
    ([grantType], schema) => (
      grantType as TGrantTypeKey === 'RATE'
        ? schema
          .moreThan(0)
          .max(100)
          .transform((value, originalValue) => (originalValue === '' ? undefined : value))
          .typeError(({ label }) => `${label}は小数第1位までの数値を入力してください。`)
          .test(
            'isFirstDecimalPlace',
            ({ label }) => `${label}は小数第1位までの数値を入力してください。`,
            (value) => !(`${value}`)
              .match(/\.[0-9][0-9]/),
          )
          .required()
        : schema
    ),
  );

/** 付与数 */
export const amountSchema = number()
  .label('付与数')
  .transform((value, originalValue) => (typeof originalValue === 'number' ? value : undefined))
  .when(
    'grantType',
    ([grantType], schema) => (
      grantType as TGrantTypeKey === 'AMOUNT'
        ? schema.required()
          .integer()
          .min(1)
          .max(99999999)
          .transform((value, originalValue) => (originalValue === '' ? undefined : value))
          .typeError(({ label }) => `${label}は整数で入力してください。`)
        : schema
    ),
  );

/** 有効期限区分 */
export const expiredPatternSchema = string<ExpiredPatternKey>()
  .label('有効期限区分')
  .required(({ label }) => `${label}を選択してください。`);

/** 有効期限（日数）:「付与日からN日後」の「N」 */
export const dayCountSchema = number()
  .label('有効期限')
  .transform((value, originalValue) => (typeof originalValue === 'number' ? value : undefined))
  .when(
    'expiredPattern',
    ([expiredPattern], schema) => (
      expiredPattern as ExpiredPatternKey === 'DAYS_LATER'
        ? schema.integer()
          .min(1, ({
            label,
            min,
          }) => `${label}は「${min}日後」以上に設定してください。`)
          .max(1825, ({
            label,
            max,
          }) => `${label}は「${max}日後」以下に設定してください。`)
          .transform((value, originalValue) => (originalValue === '' ? undefined : value))
          .typeError(({ label }) => `${label}は整数で入力してください。`)
          .required()
        : schema
    ),
  );

/** 有効期限（月数）:「付与日からNか月後の月末」の「N」 */
export const monthCountSchema = number()
  .label('有効期限')
  .transform((value, originalValue) => (typeof originalValue === 'number' ? value : undefined))
  .when(
    'expiredPattern',
    ([expiredPattern], schema) => (
      expiredPattern as ExpiredPatternKey === 'MONTHS_LATER_MONTH_END'
        ? schema.required()
        : schema
    ),
  );

/** 有効期限（年数）:「付与日からN年後のM月末」の「N」 */
export const yearCountSchema = number()
  .label('有効期限')
  .transform((value, originalValue) => (typeof originalValue === 'number' ? value : undefined))
  .when(
    'expiredPattern',
    ([expiredPattern], schema) => (
      expiredPattern as ExpiredPatternKey === 'YEARS_LATER_SPECIFIC_MONTH_END'
        ? schema.required()
        : schema
    ),
  );

/** 有効期限（指定月）:「付与日からN年後のM月末」の「M」 */
export const baseMonthSchema = number()
  .label('有効期限')
  .transform((value, originalValue) => (typeof originalValue === 'number' ? value : undefined))
  .when(
    'expiredPattern',
    ([expiredPattern], schema) => (
      expiredPattern as ExpiredPatternKey === 'YEARS_LATER_SPECIFIC_MONTH_END'
        ? schema.required()
        : schema
    ),
  );

/** 有効期限（指定年月日） */
export const specifiedDateSchema = date()
  .label('有効期限')
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined))
  .when(
    'expiredPattern',
    ([expiredPattern], schema) => (
      expiredPattern as ExpiredPatternKey === 'SPECIFICATION'
        ? schema.required()
        : schema
    ),
  );

export const grantConditionArrayDatumSchema = object({
  amountFrom: amountFromSchema,
  amountTo: amountToSchema,
  grantType: grantTypeSchema,
  rate: rateSchema,
  amount: amountSchema,
})
  .required();

export type GrantConditionArrayDatum = InferType<typeof grantConditionArrayDatumSchema>;

export const grantConditionSchema = array(grantConditionArrayDatumSchema)
  .default([])
  .min(1)
  .test(
    '付与区分が重複していないか',
    () => '重複している付与条件があります。',
    (value) => {
      const sortedGrantConditions = value
        // HACK: item is typeで上手く書きたい
        .filter((item) => !(item.amountFrom == null))
        .sort((a, b) => {
          // 昇順ソート
          if (a.amountFrom! < b.amountFrom!) return -1;
          if (a.amountFrom! > b.amountFrom!) return 1;
          return 0;
        });

      const loopTimes = sortedGrantConditions.length - 1;

      for (let i = 0; i < loopTimes; i += 1) {
        const nextAmountFrom = sortedGrantConditions[i + 1].amountFrom!;
        const { amountTo } = sortedGrantConditions[i];

        if (amountTo != null && nextAmountFrom <= amountTo) {
          return false;
        }
      }
      return true;
    },
  );

export const pointExpireSchema = object({
  expiredPattern: expiredPatternSchema,
  dayCount: dayCountSchema,
  monthCount: monthCountSchema,
  yearCount: yearCountSchema,
  baseMonth: baseMonthSchema,
  specifiedDate: specifiedDateSchema,
});

export const pointInfoSchema = object({
  useStartPattern: useStartPatternSchema,
  useStartAddValue: useStartAddValueSchema,
  accountType: accountTypeSchema,
  grantCondition: grantConditionSchema,
  pointExpire: pointExpireSchema,
});

export const campaignTargetSchema = object({
  actionType: actionTypeSchema,
  houseFlg: houseFlgSchema,
  smartCodeFlg: smartCodeFlgSchema,
  startDateTime: campaignStartDateTimeSchema,
  endDateTime: campaignEndDateTimeSchema,
  companies: companiesSchema,
  targetSelection: targetSelectionSchema,
  shops: shopsSchema,
  walletServices: walletServicesSchema,
  fileName: campaignFileNameSchema,
})
  .test(
    '企業・店舗選択時に、企業または店舗が選択されているか',
    () => '企業、または店舗を選択してください。',
    ({
      targetSelection,
      shops,
      companies,
    }) => !(targetSelection === 'COMPANY_OR_SHOP' && companies.length === 0 && shops.length === 0),
  )
  .test(
    'CSV一括アップロード選択時にCSVがアップロードされているか',
    () => 'CSVファイルをアップロードしてください。',
    ({
      targetSelection,
      fileName,
    }) => !(targetSelection === 'CSV_UPLOAD' && fileName == null),
  );

export const campaignFormSchema = object({
  publicName: campaignNameSchema,
  name: systemNameSchema,
  remarks: remarksSchema(),
  campaignTarget: campaignTargetSchema,
  pointInfo: pointInfoSchema,
})
  .test(
    'キャンペーン開始日より後か',
    () => '有効期限は「キャンペーン開始日」より後に設定してください。',
    ({
      campaignTarget,
      pointInfo,
    }) => {
      const { startDateTime } = campaignTarget;
      const { specifiedDate } = pointInfo.pointExpire;
      const expiredPattern = pointInfo.pointExpire.expiredPattern as ExpiredPatternKey;

      if (!(expiredPattern === 'SPECIFICATION' && startDateTime && specifiedDate)) return true;

      return startDateTime.getTime() < specifiedDate.getTime();
    },
  )
  .test(
    'キャンペーン終了日より後か',
    () => '有効期限は「キャンペーン終了日」より後に設定してください。',
    ({
      campaignTarget,
      pointInfo,
    }) => {
      const { endDateTime } = campaignTarget;
      const { specifiedDate } = pointInfo.pointExpire;
      const expiredPattern = pointInfo.pointExpire.expiredPattern as ExpiredPatternKey;

      if (!(expiredPattern === 'SPECIFICATION' && endDateTime && specifiedDate)) return true;

      return endDateTime.getTime() < specifiedDate.getTime();
    },
  );

export type CampaignForm = InferType<typeof campaignFormSchema>;

/** キャンペーン編集（開始日時） */
export const editCampaignStartDateTime = date()
  .label('開始日時')
  .required()
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined));

export const editCampaignEndDateTime = date()
  .label('終了日時')
  .required()
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined))
  .when(
    'startDateTime',
    ([startDateTime], schema) => {
      const today = new Date();
      const minDateTime = startDateTime.getTime() < today.getTime() ? today : startDateTime;

      return schema
        .min(minDateTime)
        .test(
          'isMoreThanStartDate',
          '終了日時は開始日時より後の日時を入力してください。',
          (value) => (value ? startDateTime.getTime() < value.getTime() : true),
        );
    },
  );

/** キャンペーン編集用フォーム */
export const editCampaignFormSchema = object({
  startDateTime: editCampaignStartDateTime,
  endDateTime: editCampaignEndDateTime,
});

export type EditCampaignForm = InferType<typeof editCampaignFormSchema>;
